import moment from "moment-timezone";
import { BooleanParam, NumberParam, StringParam } from "use-query-params";

import { PaymentFrequency, ZmUserStatus } from "../../../types";
import { SAVE_DATE_FORMAT } from "@/v1/constants";
import { DateValues, getDateRange } from "../../common/DateFiltersPicker";
import { DateInputType } from "../../common/TransactionFilters/DateInput";
import { getBerlinMomentTimezone } from "../../common/helpers";
import { getDefaultZmDateFilters } from "./defaultDateFilters";
import { ZmFilterOptions } from "./types";

/**
 * URL persistence for the ZM list (SMT-1354).
 *
 * Two independent things live in the URL: the selection `email` (which
 * customer the sibling ELSTER tab works on, see `useEmailParam` /
 * `EMAIL_PARAM_NAME`) and the list's own state below — date range,
 * frequency, status, the "missing VAT ID" toggle, and page. None of these
 * names collide with `email`: the ZM ELSTER tab reads only `email`, never
 * any of these.
 *
 * Expanding a row only ever writes `email`; it must never touch the list
 * params, so returning from ELSTER leaves the working list exactly as it
 * was (that is the SMT-1354 bug this fixes).
 */
export const ZM_LIST_QUERY_CONFIG = {
  filterEmail: StringParam,
  dateFrom: StringParam,
  dateTo: StringParam,
  frequency: StringParam,
  status: StringParam,
  missingVatId: BooleanParam,
  page: NumberParam,
};

/** The decoded shape of `ZM_LIST_QUERY_CONFIG`, as `useQueryParams` returns it. */
export interface ZmListQuery {
  filterEmail?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  frequency?: string | null;
  status?: string | null;
  missingVatId?: boolean | null;
  page?: number | null;
}

export interface ZmDatePickerDefaults {
  defaultYear: string;
  defaultQuarter: string;
  defaultMonth: moment.Moment | null;
}

/**
 * Reverse-engineers which `DateFiltersPicker` period button (year / quarter
 * / month) would have produced the given `dateFrom`/`dateTo` range, so a
 * page reload or a tab round trip can re-select that button instead of
 * showing no period selected at all (SMT-1354).
 *
 * Tried in this order — QUARTER, MONTH, YEAR — the first whose computed
 * range matches the given dates exactly wins.
 *
 * Known ambiguity: for the running period, two selections can produce
 * byte-identical dates (e.g. with `maxDate` on 15 October, "October" and
 * "Q4" both range from 1 Oct to 15 Oct). The quarter wins in that case, and
 * the resulting list is identical either way, since both produce the same
 * date range.
 */
export const getZmDatePickerDefaults = (
  dateFrom: string,
  dateTo: string,
  maxDate: string
): ZmDatePickerDefaults => {
  const start = moment(dateFrom, SAVE_DATE_FORMAT);
  const defaultYear = start.format("YYYY");

  const quarterRange = getDateRange(
    start.format("Q.YYYY"),
    DateInputType.Quarter,
    maxDate
  );
  if (quarterRange.dateFrom === dateFrom && quarterRange.dateTo === dateTo) {
    return {
      defaultYear,
      defaultQuarter: `Q${start.quarter()}`,
      defaultMonth: null,
    };
  }

  const monthRange = getDateRange(
    start.format("MM.YYYY"),
    DateInputType.Month,
    maxDate
  );
  if (monthRange.dateFrom === dateFrom && monthRange.dateTo === dateTo) {
    return {
      defaultYear,
      defaultQuarter: "",
      defaultMonth: moment(dateFrom, SAVE_DATE_FORMAT),
    };
  }

  const yearRange = getDateRange(defaultYear, DateInputType.Year, maxDate);
  if (yearRange.dateFrom === dateFrom && yearRange.dateTo === dateTo) {
    return { defaultYear, defaultQuarter: "", defaultMonth: null };
  }

  // No period button produces this exact range (e.g. a hand-edited URL, or
  // dates from the free-form range picker) — fall back to just the year of
  // `dateFrom`, with no quarter/month selected.
  return { defaultYear, defaultQuarter: "", defaultMonth: null };
};

interface SeedZmListStateOptions {
  emailParam?: string | null;
  withinTabs: boolean;
  defaults: ReturnType<typeof getDefaultZmDateFilters>;
}

interface ZmListState {
  filterOptions: ZmFilterOptions;
  page: number;
  datePickerDefaults: ZmDatePickerDefaults;
}

const isValidSaveDate = (value?: string | null): value is string =>
  Boolean(value) && moment(value, SAVE_DATE_FORMAT, true).isValid();

const isZmUserStatus = (
  value?: string | null
): value is keyof typeof ZmUserStatus =>
  Boolean(value) && Object.keys(ZmUserStatus).includes(value as string);

const isPaymentFrequency = (value?: string | null): value is PaymentFrequency =>
  Object.values(PaymentFrequency).includes(value as PaymentFrequency);

/**
 * Build the ZM list's initial filter/page state from the URL.
 *
 * Email seeding rule (identical to VAT, see `seedVatListState`): a manually
 * typed list filter (`filterEmail`) always wins; otherwise, arriving from
 * outside the ZM tabs with a selected customer (`?email=`, e.g. from Alle
 * Mandanten or the customer drawer) filters the list to them, as today; but
 * returning from a sibling tab (`withinTabs`) must not re-narrow the list to
 * the selection.
 *
 * The other URL values are hand-editable, so they are validated before they
 * reach the list: `dateFrom`/`dateTo` fall back to `defaults` unless both
 * are valid `SAVE_DATE_FORMAT` dates with `dateFrom <= dateTo`;
 * `frequency`/`status` fall back to `undefined` when they are not real enum
 * values; `page` falls back to 1 when it is not a positive integer.
 */
export const seedZmListState = (
  query: ZmListQuery,
  { emailParam, withinTabs, defaults }: SeedZmListStateOptions
): ZmListState => {
  const email = query.filterEmail ?? (withinTabs ? "" : emailParam ?? "");

  const datesValid =
    isValidSaveDate(query.dateFrom) &&
    isValidSaveDate(query.dateTo) &&
    !moment(query.dateFrom as string, SAVE_DATE_FORMAT).isAfter(
      moment(query.dateTo as string, SAVE_DATE_FORMAT)
    );

  // `defaults` (from `getDefaultZmDateFilters()`) always has real dates —
  // `DateValues` only declares them optional for the generic
  // `getDateRange` case.
  const dateFrom = datesValid
    ? (query.dateFrom as string)
    : (defaults.dateFrom as string);
  const dateTo = datesValid
    ? (query.dateTo as string)
    : (defaults.dateTo as string);

  const vatPaymentFrequency = isPaymentFrequency(query.frequency)
    ? query.frequency
    : undefined;

  const status = isZmUserStatus(query.status) ? query.status : undefined;

  const missingRecipientVatId = query.missingVatId === true;

  const page =
    typeof query.page === "number" &&
    Number.isInteger(query.page) &&
    query.page > 0
      ? query.page
      : 1;

  const isDefaultDates =
    dateFrom === defaults.dateFrom && dateTo === defaults.dateTo;

  const datePickerDefaults: ZmDatePickerDefaults = isDefaultDates
    ? {
        defaultYear: defaults.defaultYear,
        defaultQuarter: defaults.defaultQuarter,
        defaultMonth: defaults.defaultMonth,
      }
    : getZmDatePickerDefaults(
        dateFrom,
        dateTo,
        getBerlinMomentTimezone().format()
      );

  return {
    filterOptions: {
      email,
      vatPaymentFrequency,
      dateFrom,
      dateTo,
      status,
      missingRecipientVatId,
    },
    page,
    datePickerDefaults,
  };
};

const isCustomRange = (
  filterOptions: ZmFilterOptions,
  defaults: DateValues
): boolean =>
  Boolean(filterOptions.dateFrom) &&
  Boolean(filterOptions.dateTo) &&
  (filterOptions.dateFrom !== defaults.dateFrom ||
    filterOptions.dateTo !== defaults.dateTo);

/**
 * Map the ZM list's current filters/page to query params for `setQuery`.
 * Values equal to their default are `undefined` so they leave the URL.
 */
export const toZmListQuery = (
  filterOptions: ZmFilterOptions,
  page: number,
  defaults: DateValues
): ZmListQuery => ({
  filterEmail: filterOptions.email || undefined,
  // The range is one value, so both boundaries are written together: a
  // custom period can share one boundary with the default (picking July
  // while the default is Q3 keeps the same dateFrom), and a half-written
  // pair is rejected by `seedZmListState`, which then restores the
  // defaults on the next tab return.
  ...(isCustomRange(filterOptions, defaults)
    ? { dateFrom: filterOptions.dateFrom, dateTo: filterOptions.dateTo }
    : { dateFrom: undefined, dateTo: undefined }),
  frequency: filterOptions.vatPaymentFrequency ?? undefined,
  status: filterOptions.status ?? undefined,
  missingVatId: filterOptions.missingRecipientVatId ? true : undefined,
  page: page > 1 ? page : undefined,
});
